import {
  SelectPlanCTAText,
} from '../../../../../../../../../shared/design-system/components/molecules/pricing-card/types';
import { BillingOption } from '../../../../../../../../../shared/utils/billing-options';
import { GetPlansResponse } from '../../../../../types';
import { checkIsDemoPlanUser } from '../../../../../../../../../shared/design-system/components/molecules/subscription-plan-details/helper/helper';

const starterFeatures = [
  {
    label: '1,000 Credits per Month',
    labelClass: 'font-semibold',
    highlight: true,
  },
  {
    label: '1 Phone Number Included',
    labelClass: 'font-semibold',
  },
  {
    label: 'Phone Number Purchase and Assignment',
  },
  {
    label: 'Call Recording and Transcriptions',
  },
  {
    label: 'AI Call Summary',
  },
  {
    label: 'Voicemail Templates',
  },
  {
    label: 'Pre-call audio Testing',
  },
];

const proFeatures = [
  {
    label: 'Everything in Starter Plan',
    labelClass: 'font-semibold',
    highlight: true,
  },
  {
    label: '2,500 Credits per Month',
    labelClass: 'font-semibold',
    highlight: true,
  },
  {
    label: '3 Phone Numbers Included',
    labelClass: 'font-semibold',
  },
  {
    label: 'Power Dialer (Coming Soon)',
  },
  {
    label: 'Parallel Dialer (Coming Soon)',
  },
  {
    label: 'Mobile Dialer App (Coming Soon)',
  },
];

const planMetaData = {
  'dialer-starter-monthly': {
    displayName: 'Dialer Starter',
    features: starterFeatures,
  },
  'dialer-starter-quarterly': {
    displayName: 'Dialer Starter',
    features: starterFeatures,
  },
  'dialer-starter-yearly': {
    displayName: 'Dialer Starter',
    features: starterFeatures,
  },
  'dialer-pro-monthly': {
    displayName: 'Dialer Pro',
    features: proFeatures,
    isBestValuePlan: true,
  },
  'dialer-pro-quarterly': {
    displayName: 'Dialer Pro',
    features: proFeatures,
    isBestValuePlan: true,
  },
  'dialer-pro-yearly': {
    displayName: 'Dialer Pro',
    features: proFeatures,
    isBestValuePlan: true,
  },
};

export const getDialerPlanMetaDataByCode = (planCode) =>
  planMetaData[planCode] || null;

export const getDialerPlanMetaDataByName = (planName) => {
  if (!planName) return null;
  if (planName.includes('Pro')) {
    return {
      displayName: 'Dialer Pro',
      features: proFeatures,
      isBestValuePlan: true,
    };
  }
  if (planName.includes('Starter')) {
    return {
      displayName: 'Dialer Starter',
      features: starterFeatures,
    };
  }
  return null;
};

export const getDialerPlanMetaData = (planName) =>
  getDialerPlanMetaDataByName(planName);

export const getPlanPrice = (planPrice, planMode) =>
  Number(planPrice) / Number(planMode);

const extractDialerPlanTier = (
  planCode: string,
): 'starter' | 'pro' | null => {
  if (planCode?.includes('starter')) return 'starter';
  if (planCode?.includes('pro')) return 'pro';
  return null;
};

const dialerPlanTierHierarchy = ['starter', 'pro'];

export const getButtonLabel = ({
  plan,
  sendGetDialerSubscriptionResponse,
  planCycle,
}: {
  plan: any;
  sendGetDialerSubscriptionResponse: any;
  planCycle?: BillingOption;
}) => {
  if (checkIsDemoPlanUser()) {
    return SelectPlanCTAText.UpgradePlan;
  }

  const {
    planMode,
    planCode,
  } = sendGetDialerSubscriptionResponse || {};

  const currentTier = extractDialerPlanTier(planCode);
  const selectedTier = extractDialerPlanTier(plan?.planCode || '');

  const currentIndex = dialerPlanTierHierarchy.indexOf(currentTier || '');
  const selectedIndex = dialerPlanTierHierarchy.indexOf(selectedTier || '');

  if (currentTier === selectedTier) {
    // for monthly plan
    if (planMode === 1) {
      if (planCycle === BillingOption.MONTHLY) {
        return SelectPlanCTAText.CurrentPlan;
      }
      if (
        planCycle === BillingOption.QUARTERLY ||
        planCycle === BillingOption.ANNUALLY
      ) {
        return SelectPlanCTAText.UpgradePlan;
      }
    }
    // for quarterly plan
    if (planMode === 3) {
      if (planCycle === BillingOption.QUARTERLY) {
        return SelectPlanCTAText.CurrentPlan;
      }
      if (planCycle === BillingOption.ANNUALLY) {
        return SelectPlanCTAText.UpgradePlan;
      }
      if (planCycle === BillingOption.MONTHLY) {
        return SelectPlanCTAText.DowngradePlan;
      }
    }
    // for yearly plan
    if (planMode === 12) {
      if (planCycle === BillingOption.ANNUALLY) {
        return SelectPlanCTAText.CurrentPlan;
      }
      if (
        planCycle === BillingOption.MONTHLY ||
        planCycle === BillingOption.QUARTERLY
      ) {
        return SelectPlanCTAText.DowngradePlan;
      }
    }

    return SelectPlanCTAText.CurrentPlan;
  }

  if (selectedIndex < currentIndex) {
    return SelectPlanCTAText.DowngradePlan;
  }

  if (selectedIndex > currentIndex) {
    return SelectPlanCTAText.UpgradePlan;
  }

  return SelectPlanCTAText.UpgradePlan;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getPlanDetailsObj = (plan, _isUserSubscribed) => {
  const metaData =
    planMetaData[plan?.planCode] ||
    getDialerPlanMetaDataByName(plan?.planName);
  return {
    id: plan?.id || null,
    planName: plan?.planName,
    planCode: plan?.planCode,
    displayName: metaData?.displayName || plan?.planName,
    // isBestValuePlan: metaData?.isBestValuePlan || false,
    isBestValuePlan: false,
    features: metaData?.features || [],
    price: getPlanPrice(plan?.amount, plan?.planMode) || null,
    ...plan,
  };
};

export const getDialerPlanMonthlyPrice = (
  planCode: string,
  plans: GetPlansResponse,
) => {
  const correspondingMonthlyPlanCode =
    planCode
      ?.replace('yearly', 'monthly')
      ?.replace('quarterly', 'monthly') || '';

  const correspondingMonthlyPlan = plans?.find(
    (plan) => plan.planCode === correspondingMonthlyPlanCode,
  );

  if (correspondingMonthlyPlan) {
    return parseInt(correspondingMonthlyPlan?.amount || '0', 10);
  }

  return 0;
};

export const getDialerPlanYearlyPrice = (
  planCode: string,
  plans: GetPlansResponse,
) => {
  const correspondingYearlyPlanCode =
    planCode
      ?.replace('monthly', 'yearly')
      ?.replace('quarterly', 'yearly') || '';

  const correspondingYearlyPlan = plans?.find(
    (plan) => plan.planCode === correspondingYearlyPlanCode,
  );

  if (correspondingYearlyPlan) {
    return parseInt(correspondingYearlyPlan?.amount || '0', 10);
  }

  return 0;
};

export const getDialerPlanQuarterlyPrice = (
  planCode: string,
  plans: GetPlansResponse,
) => {
  const correspondingQuarterlyPlanCode =
    planCode
      ?.replace('yearly', 'quarterly')
      ?.replace('monthly', 'quarterly') || '';

  const correspondingQuarterlyPlan = plans?.find(
    (plan) => plan.planCode === correspondingQuarterlyPlanCode,
  );

  if (correspondingQuarterlyPlan) {
    return parseInt(correspondingQuarterlyPlan?.amount || '0', 10);
  }

  return 0;
};

export const getIsUserInPaidDialerPlan = (planCode) =>
  planCode !== 'dialer-free' &&
  planCode !== 'free' &&
  planCode !== null &&
  planCode !== undefined &&
  planCode !== '';
